import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TablePagination,
  Avatar,
} from '@mui/material';
import { 
  Refresh, 
  Visibility,
  MiscellaneousServices 
} from '@mui/icons-material';
import apiClient from '../../services/Api';
import { useNavigate } from 'react-router-dom';

const DisplayServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  
  const navigate = useNavigate();

  const fetchServiceCategories = async (page = 1, limit = 10) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiClient.get(
        `/services/service-sub-categories/service-category?page=${page}&limit=${limit}`
      );
      
      if (response.data && response.data.success) {
        setServices(response.data.data || []);
        setTotalRecords(response?.totalRecords || response?.totalItems || response?.totalCount || response?.totalDocs || response?.total || response?.data?.totalRecords || response?.data?.totalItems || response?.data?.totalCount || response?.data?.totalDocs || response?.data?.total || response?.data?.totalElements || response?.data?.length || response.data.totalRecords || 0 || 0);
      }
    } catch (err) {
      console.error('Error fetching service categories:', err);
      setError(err.response?.data?.message || 'Failed to fetch service categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServiceCategories();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchServiceCategories(newPage + 1, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchServiceCategories(1, newRowsPerPage);
  };

  const handleViewSubServices = (serviceId) => {
    // Navigate to sub-services page for this service
    navigate(`/services/${serviceId}/sub-services`);
  };

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 300, color: 'text.primary' }}>
            Service Categories
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage and view service sub-categories
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <IconButton onClick={() => fetchServiceCategories(page + 1, rowsPerPage)} disabled={loading} sx={{ color: 'primary.main', border: '1px solid', borderColor: 'divider' }}>
            {loading ? <CircularProgress size={24} color="inherit" /> : <Refresh />}
          </IconButton>
          {/* <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/services/create')}
            sx={{ px: 4, backgroundColor: 'primary.main' }}
          >
            Add Service
          </Button> */}
        </Box>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      ) : null}

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'text.border', borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="services table">
            <TableHead sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Image</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Order</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : services.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                    <MiscellaneousServices sx={{ fontSize: 40, color: 'text.secondary', opacity: 0.3, mb: 1 }} />
                    <Typography color="text.secondary">
                      No service categories found
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => navigate('/categories/modify')}
                      sx={{ mt: 2 }}
                    >
                      Create your first service category
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                services.map((service) => (
                  <TableRow key={service.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell>
                      <Avatar
                        variant="rounded"
                        src={service.image}
                        sx={{ width: 60, height: 40, border: '1px solid', borderColor: 'text.border' }}
                      >
                        <MiscellaneousServices />
                      </Avatar>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {service.name || 'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {service.type || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={service.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        color={service.isActive ? 'success' : 'error'}
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {service.order || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ pr: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Visibility />}
                        onClick={() => handleViewSubServices(service.id)}
                        sx={{ 
                          textTransform: 'none',
                          borderColor: 'primary.main',
                          color: 'primary.main',
                          '&:hover': {
                            borderColor: 'primary.dark',
                            backgroundColor: 'primary.50',
                          }
                        }}
                      >
                        View Sub Services
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {services.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalRecords}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>
    </Box>
  );
};

export default DisplayServices;